import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Mail, Globe2, Loader2 } from "lucide-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInstagram, faLinkedin, faYoutube, faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import { supabase } from "@/lib/supabase";
import { useSiteData } from "@/context/SiteDataContext";

type Branch = {
  id: string;
  name: string;
  address: string;
  phone: string;
  email: string;
  tag: string | null;
};

const Branches = () => {
  const [branches, setBranches] = useState<Branch[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const settings = useSiteData();

  useEffect(() => {
    supabase
      .from('Branch')
      .select('*')
      .order('order', { ascending: true })
      .then(({ data }) => {
        if (data) setBranches(data);
        setIsLoading(false);
      });
  }, []);

  if (isLoading) {
    return (
      <div className="py-32 flex justify-center">
        <Loader2 className="animate-spin text-primary" size={32} />
      </div>
    );
  }

  if (branches.length === 0) return null;

  return (
    <section id="branches" className="py-32 relative overflow-hidden bg-surface/30">
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] border border-white rounded-full" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] border border-white rounded-full" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] border border-white rounded-full" />
      </div>

      <div className="container relative z-10">
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          className="flex flex-col md:flex-row justify-between items-end mb-20 gap-8"
        >
          <div>
            <span className="flex items-center gap-2 text-primary font-bold tracking-widest uppercase text-sm mb-4">
              <Globe2 size={16} /> Global Reach
            </span>
            <h2 className="text-5xl md:text-7xl font-black mb-4 tracking-tight">OUR BRANCHES</h2>
            <p className="text-text-muted text-xl max-w-lg">Global presence with local expertise, ready to execute your vision anywhere.</p>
          </div>
          {settings?.phone && (
            <div className="text-right glass-card px-8 py-4 border-primary/20 bg-primary/5">
              <span className="text-sm text-text-muted uppercase tracking-widest block mb-1">Direct Line</span>
              <span className="text-white font-black text-2xl tracking-tighter">{settings.phone}</span>
            </div>
          )}
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8">
          {branches.map((branch, index) => (
            <motion.div
              key={branch.id}
              initial={{ opacity: 0, scale: 0.9, rotateX: 20 }}
              whileInView={{ opacity: 1, scale: 1, rotateX: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              whileHover={{ y: -10, scale: 1.02 }}
              transition={{ delay: index * 0.1, type: "spring", stiffness: 100 }}
              className="glass-card p-10 group hover:border-primary/50 hover:shadow-2xl hover:shadow-primary/20 transition-all duration-300 relative overflow-hidden"
            >
              <div className="absolute -right-20 -top-20 w-40 h-40 bg-primary/20 blur-[50px] rounded-full group-hover:scale-150 transition-transform duration-700 ease-out" />
              
              <div className="relative z-10">
                <div className="inline-block px-4 py-1.5 bg-primary/10 border border-primary/20 rounded-full text-xs font-bold text-primary mb-6 uppercase tracking-[0.2em]">
                  {branch.tag || "Branch"}
                </div>
                <h3 className="text-4xl font-black mb-8 text-white">{branch.name}</h3>
                
                <div className="space-y-6 mb-8">
                  <div className="flex gap-4 items-start cursor-default">
                    <div className="p-2 bg-white/5 rounded-lg text-primary shrink-0 group-hover:bg-primary/20 transition-colors">
                      <MapPin size={20} />
                    </div>
                    <span className="text-text-muted font-medium leading-relaxed">{branch.address}</span>
                  </div>
                  
                  {branch.phone && (
                    <div className="flex gap-4 items-center cursor-default">
                      <div className="p-2 bg-white/5 rounded-lg text-primary shrink-0 group-hover:bg-primary/20 transition-colors">
                        <Phone size={20} />
                      </div>
                      <span className="text-white font-semibold">{branch.phone}</span>
                    </div>
                  )}
                  
                  {branch.email && (
                    <div className="flex gap-4 items-center cursor-default">
                      <div className="p-2 bg-white/5 rounded-lg text-primary shrink-0 group-hover:bg-primary/20 transition-colors">
                        <Mail size={20} />
                      </div>
                      <span className="text-white font-semibold">{branch.email}</span>
                    </div>
                  )}
                </div>

                {/* Social Icons for each branch */}
                <div className="flex gap-6 pt-6 border-t border-white/5 opacity-60 group-hover:opacity-100 transition-opacity">
                  {settings?.whatsapp && (
                    <a href={settings.whatsapp.startsWith("http") ? settings.whatsapp : `https://wa.me/${settings.whatsapp.replace(/\D/g, "")}`} target="_blank" rel="noopener noreferrer" className="text-text-muted hover:text-green-500 transition-colors">
                      <FontAwesomeIcon icon={faWhatsapp} size="lg" />
                    </a>
                  )}
                  {settings?.instagram && (
                    <a href={settings.instagram} target="_blank" rel="noopener noreferrer" className="text-text-muted hover:text-pink-500 transition-colors">
                      <FontAwesomeIcon icon={faInstagram} size="lg" />
                    </a>
                  )}
                  {settings?.linkedin && (
                    <a href={settings.linkedin} target="_blank" rel="noopener noreferrer" className="text-text-muted hover:text-blue-500 transition-colors">
                      <FontAwesomeIcon icon={faLinkedin} size="lg" />
                    </a>
                  )}
                  {settings?.youtube && (
                    <a href={settings.youtube} target="_blank" rel="noopener noreferrer" className="text-text-muted hover:text-red-500 transition-colors">
                      <FontAwesomeIcon icon={faYoutube} size="lg" />
                    </a>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Branches;
